import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * useUnreadCounters — single shared hook that polls notification + message counters.
 * Uses module-level state so multiple consumers share the same value without re-fetching.
 */
let _state = { messages: 0, notifications: 0 };
const _listeners = new Set();
let _interval = null;
let _refCount = 0;

function notify() {
  for (const fn of _listeners) fn(_state);
}

async function refresh() {
  try {
    const [m, n] = await Promise.allSettled([
      api.get("/messages/unread-count"),
      api.get("/notifications/unread-count"),
    ]);
    const messages = m.status === "fulfilled" ? (m.value.data?.count ?? 0) : _state.messages;
    const notifications = n.status === "fulfilled" ? (n.value.data?.count ?? 0) : _state.notifications;
    _state = { messages, notifications };
    notify();
  } catch { /* swallow */ }
}

export function refreshUnreadCounters() { return refresh(); }

export function useUnreadCounters(enabled = true) {
  const [val, setVal] = useState(_state);

  useEffect(() => {
    if (!enabled) return;
    _listeners.add(setVal);
    _refCount += 1;
    refresh(); // immediate fetch on first mount
    if (!_interval) {
      _interval = setInterval(refresh, 25000);
    }
    return () => {
      _listeners.delete(setVal);
      _refCount -= 1;
      if (_refCount <= 0 && _interval) {
        clearInterval(_interval);
        _interval = null;
        _refCount = 0;
      }
    };
  }, [enabled]);

  return val;
}
